import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import 'react-phone-number-input/style.css';
import API from '../../utils/API';
import constants from '../../utils/constants';
import ModalPasswordInput from '../../components/Modals/ModalInputs/ModalPasswordInput';
import commonFunctions from '../../utils/commonFunctions';

function ChangePasswordModal(props) {
  const { closeModal } = commonFunctions;
  const {
    handleCleanUp
  } = props;

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [oldPasswordValid, setOldPasswordValid] = useState(true);
  const [newPasswordValid, setNewPasswordValid] = useState(true); 
  const [passwordLength, setPasswordLength] = useState(8);
  const [passwordPolicy, setPasswordPolicy] = useState(false);

  useEffect(() => {
    API.getGlobalPolicies()
      .then(({ data }) => {
        const passwordSetting = data.find(
          (setting) => setting.global_policy_id === constants.globalPolicies.password
        );
        if (passwordSetting) {
          setPasswordPolicy(true);
          setPasswordLength(passwordSetting.settings.minimumLength);
        }
      })
      .catch((err) => {
        console.error(err.message);
      });
  }, []);

  const validateInputs = async () => {
    let isValid = true;
    const errors = {};

    if (!oldPassword.trim()) {
      errors.oldPassword = <FormattedMessage id="FIELD_CANNOT_EMPTY" />;
      setOldPasswordValid(false); // Set validity state
      isValid = false;
    } else {
      setOldPasswordValid(true);
    }

    if (newPassword.trim().length < passwordLength) {
      errors.newPassword = <FormattedMessage id="PASSWORD_NOT_MEET_MINI_LENGTH" />;
      setNewPasswordValid(false); // Set validity state
      isValid = false;
    } else if (oldPassword.trim() === newPassword.trim()) {
      errors.newPassword = <FormattedMessage id="PASSWORD_CANNOT_SAME" />;
      setNewPasswordValid(false); 
      isValid = false;

    } else {
      setNewPasswordValid(true);
    }

    setErrors(errors);
    setSubmitted(true);

    if (isValid) {
      return await handlePasswordSubmit();
    }
    return false;
  };

  const handlePasswordSubmit = async () => {
    // console.log('oldPassword:', oldPassword.trim(), 'newPassword:', newPassword.trim());
    try {
      await API.changePassword(oldPassword.trim(), newPassword.trim());
      clearPasswordModal();
      closeModal('#changePasswordModal');
      handleCleanUp(); // clean up user session
      return true;
    } catch (err) {
      console.log(err);

      // Check for 401 error and handle it locally to prevent the interceptor from triggering
      if (err.response && err.response.status === 401) {
        // Handle incorrect password case
        if (err.response.data === 'Incorrect password') {
          // console.log('Incorrect password');
          setErrorMessage(<FormattedMessage id="CURRENT_PASSWORD_INCORRECT" />);
          setOldPasswordValid(false); // Set the old password field as invalid
          setErrors((prevErrors) => ({
            ...prevErrors,
            oldPassword: <FormattedMessage id="CURRENT_PASSWORD_INCORRECT" />,
          }));
          return false;
        }
      }

      setErrors({ newPassword: <FormattedMessage id="INVALID_PASSWORD" /> });
      setErrorMessage(<FormattedMessage id="INVALID_PASSWORD" />);
    }
  };

  const handleSubmit = async (e) => {
    console.log('handle submit called')
    e.preventDefault();
    await validateInputs();
  };

  function clearPasswordModal(e) {
    setErrors({});
    setOldPassword("");
    setNewPassword("");
  }

  const onClickClose = (e) => {
    setSubmitted(false);
    setErrors({});
    setErrorMessage('');
    clearPasswordModal(e);
  };

  return (
    <div
      className="modal fade"
      id="changePasswordModal"
      tabIndex="-1"
      aria-labelledby="changePasswordModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <FormattedMessage id="CHANGE_PASSWORD" />
            </h5>
            <button
              type="button"
              onClick={onClickClose}
              id="password-btn-close"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="oldPassword" className="col-form-label">
                  <FormattedMessage id="CURRENT_PASSWORD" />
                  <FormattedMessage id="SYMBOL_COLON" />
                </label>
                <ModalPasswordInput
                  onChange={(e) => setOldPassword(e.target.value)}
                  value={oldPassword}
                  name="oldPassword"
                  id="oldPasswordInput"
                  isSubmitted={submitted}
                  isValid={oldPasswordValid}
                  className={`form-control ${
                    submitted && (!errors.oldPassword ? 'is-valid' : 'is-invalid')
                  }`}
                />
                {errors.oldPassword && <div className="text-danger">{errors.oldPassword}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="newPassword" className="col-form-label">
                  <FormattedMessage id="NEW_PASSWORD" />
                  <FormattedMessage id="SYMBOL_COLON" />
                </label>
                {passwordPolicy && (
                  <div className="text-secondary">
                    <FormattedMessage id="MINIMUM_LENGTH" />
                    <FormattedMessage id="SYMBOL_COLON" /> {passwordLength}
                  </div>
                )}
                <ModalPasswordInput
                  onChange={(e) => setNewPassword(e.target.value)}
                  value={newPassword}
                  name="newPassword"
                  id="newPasswordInput"
                  isSubmitted={submitted}
                  isValid={newPasswordValid}
                  className={`form-control ${
                    submitted && (!errors.newPassword ? 'is-valid' : 'is-invalid')
                  }`}
                />
                {errors.newPassword && <div className="text-danger">{errors.newPassword}</div>}
              </div>
            </form>
            <div className="text-secondary">
              <FormattedMessage id="LOGGED_OUT_SUCCESS" />
            </div>
          </div>
          <div className="modal-footer">
            <button id="savePasswordBtn" type="button" className="btn save-btn" onClick={handleSubmit}>
              <FormattedMessage id="DONE_BTN" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChangePasswordModal;
